/**
 * lib/missingMiddle — THE single frontend source of truth for the
 * Missing Middle pillar → condition-stage doctrine.
 *
 * Mirrored 1:1 by the backend at config/missing_middle.py. Any change to
 * pillar membership, weights, the stage mapping, or the band thresholds
 * happens HERE (and the matching backend file) — nowhere else.
 * `lib/leiConditions.js` and `lib/journeySignals.js` both import from
 * this module (they previously kept parallel copies that drifted).
 *
 * User-approved pillar → stage mapping:
 *   Capability   ← P3 Capability & Readiness
 *   Visibility   ← P1 Access & Engagement
 *   Confidence   ← P2 Performance & Outcomes
 *   Coordination ← P4 Efficiency
 *   Value        ← P5 Business Impact
 */

export const PILLAR_MEASURES = {
  P1: ['training_scope', 'sme_training', 'community_learning', 'voluntary_engagement', 'tech_utilization', 'learning_culture'],
  P2: ['training_performance', 'nps', 'skills_assessment', 'certification_rate', 'course_completion'],
  P3: ['skills_readiness', 'time_to_competency', 'ojt_progression', 'career_development', 'personal_development'],
  P4: ['delivery_efficiency', 'efficiency_scaling', 'kirkpatrick_efficiency', 'management_efficiency', 'program_dev_efficiency', 'program_shelf_life'],
  P5: ['business_impact_correlation', 'learning_transfer_readiness', 'manager_coaching', 'productivity_acceleration', 'sustained_behavior_change'],
};

export const PILLAR_WEIGHTS = { P1: 0.20, P2: 0.30, P3: 0.20, P4: 0.20, P5: 0.10 };

export const PILLAR_STAGE = { P3: 'capability', P1: 'visibility', P2: 'confidence', P4: 'coordination', P5: 'value' };

// The five condition stages, in journey order, with display meta.
export const CONDITION_STAGES = [
  { key: 'capability', label: 'Capability', conditionLabel: 'Capability Conditions', pillarId: 'P3', color: '#6366f1' },
  { key: 'visibility', label: 'Visibility', conditionLabel: 'Visibility Conditions', pillarId: 'P1', color: '#8b5cf6' },
  { key: 'confidence', label: 'Confidence', conditionLabel: 'Confidence Conditions', pillarId: 'P2', color: '#f59e0b' },
  { key: 'coordination', label: 'Coordination', conditionLabel: 'Coordination Conditions', pillarId: 'P4', color: '#10b981' },
  { key: 'value', label: 'Value', conditionLabel: 'Value Conditions', pillarId: 'P5', color: '#e11d48' },
];

export const SIGNAL_STAGES = CONDITION_STAGES.map((s) => s.key);
export const STAGE_LABELS = Object.fromEntries(CONDITION_STAGES.map((s) => [s.key, s.label]));
export const STAGE_COLORS = Object.fromEntries(CONDITION_STAGES.map((s) => [s.key, s.color]));

// Journey-stage key → Navigation Readiness Diagnostic component tab.
export const STAGE_DIAGNOSTIC_TAB = { capability: 'capability', visibility: 'lei', confidence: 'motivation', coordination: 'retention' };

// Derived lookups.
export const MEASURE_PILLAR = Object.fromEntries(
  Object.entries(PILLAR_MEASURES).flatMap(([pid, ms]) => ms.map((m) => [m, pid])),
);
export const MEASURE_STAGE_MAP = Object.fromEntries(
  Object.entries(MEASURE_PILLAR).map(([mid, pid]) => [mid, PILLAR_STAGE[pid]]),
);
// Per-stage navigation weight = its pillar's LEI weight (composite = overall LEI).
export const NAV_WEIGHTS = Object.fromEntries(
  SIGNAL_STAGES.map((st) => {
    const pid = Object.keys(PILLAR_STAGE).find((p) => PILLAR_STAGE[p] === st);
    return [st, PILLAR_WEIGHTS[pid] || 0];
  }),
);

/** Descriptive condition band — plain-English, executive-readable. */
export function conditionBand(score) {
  if (score == null || Number.isNaN(score)) return { label: '—', tone: 'slate' };
  if (score >= 80) return { label: 'Strong', tone: 'emerald' };
  if (score >= 70) return { label: 'Healthy', tone: 'teal' };
  if (score >= 60) return { label: 'Stable', tone: 'amber' };
  if (score >= 50) return { label: 'Emerging', tone: 'orange' };
  return { label: 'Constrained', tone: 'rose' };
}

export const BAND_TONE = {
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  rose: 'bg-rose-100 text-rose-700 border-rose-200',
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
};
